import React from "react";
import {
    Page,
    Text,
    View,
    Image,
    Document,
    StyleSheet,
} from "@react-pdf/renderer";

const styles = StyleSheet.create({
    page: {
        padding: 16,
        fontSize: 11,
        fontFamily: "Helvetica",
    },
    header: {
        flexDirection: "row",
        alignItems: "center",
        marginBottom: 16,
    },
    profilePic: {
        width: 70,
        height: 70,
        borderRadius: 35,
        marginRight: 15,
        objectFit: "cover",
    },
    headerText: {
        flexDirection: "column",
    },
    name: {
        fontSize: 16,
        fontWeight: "bold",
    },
    section: {
        marginBottom: 12,
    },
    title: {
        fontSize: 12,
        fontWeight: "bold",
        marginBottom: 6,
        backgroundColor: "#f2f7fb",
        padding: 4,
    },
    row: {
        flexDirection: "row",
        marginBottom: 2,
        padding: "4px 0",
        borderBottom: "1px solid #F1F5F9",
    },
    label: {
        width: 140,
        fontWeight: "bold",
    },
    value: {
        flex: 1,
    },
});

interface ResumeProps {
    data: any;
}

const ResumePDF: React.FC<ResumeProps> = ({ data }) => {
    if (!data) return null;
    return (
        <Document>
            <Page size="A4" style={styles.page}>
                {/* Header with Profile Picture */}
                <View style={styles.header}>
                    {data.profilePhoto && (
                        <Image src={data.profilePhoto} style={styles.profilePic} />
                    )}
                    <View style={styles.headerText}>
                        <Text style={styles.name}>
                            {data?.firstName} {data?.lastName}
                        </Text>
                    </View>
                </View>
                {/* Personal Information */}
                <View style={styles.section}>
                    <Text style={styles.title}>Personal information</Text>
                    <View style={styles.row}>
                        <Text style={styles.label}>Phone number</Text>
                        <Text style={styles.value}>{data.phoneNumber}</Text>
                    </View>
                    <View style={styles.row}>
                        <Text style={styles.label}>Preferred name</Text>
                        <Text style={styles.value}>{data.preferrdName}</Text>
                    </View>
                    <View style={styles.row}>
                        <Text style={styles.label}>Email</Text>
                        <Text style={styles.value}>{data.email}</Text>
                    </View>
                    <View style={styles.row}>
                        <Text style={styles.label}>DOB</Text>
                        <Text style={styles.value}>{data.birthdate}</Text>
                    </View>
                    <View style={styles.row}>
                        <Text style={styles.label}>City</Text>
                        <Text style={styles.value}>{data.city}</Text>
                    </View>
                    <View style={styles.row}>
                        <Text style={styles.label}>State</Text>
                        <Text style={styles.value}>{data.state}</Text>
                    </View>
                    <View style={styles.row}>
                        <Text style={styles.label}>Country</Text>
                        <Text style={styles.value}>{data.country}</Text>
                    </View>
                    <View style={styles.row}>
                        <Text style={styles.label}>Zip code</Text>
                        <Text style={styles.value}>{data.zipCode}</Text>
                    </View>
                </View>

                {/* Salary */}
                <View style={styles.section}>
                    <Text style={styles.title}>Salary & visa</Text>
                    <View style={styles.row}>
                        <Text style={styles.label}>Salary expectation</Text>
                        <Text style={styles.value}>
                            {data.desiredAnnualSalaryRange}$
                        </Text>
                    </View>
                    <View style={styles.row}>
                        <Text style={styles.label}>H-1B visa requirement</Text>
                        <Text style={styles.value}>{data.requireSponsorshipVisa}</Text>
                    </View>
                </View>

                {/* Work Experience */}
                {data.workExperience?.map((job: any, idx: number) => (
                    <View key={idx} style={styles.section}>
                        <Text style={styles.title}>Job {idx + 1}</Text>
                        <View style={styles.row}>
                            <Text style={styles.label}>Job title</Text>
                            <Text style={styles.value}>{job.jobTitle}</Text>
                        </View>
                        <View style={styles.row}>
                            <Text style={styles.label}>Company</Text>
                            <Text style={styles.value}>{job.company}</Text>
                        </View>
                        <View style={styles.row}>
                            <Text style={styles.label}>Location</Text>
                            <Text style={styles.value}>{job.location}</Text>
                        </View>
                        <View style={styles.row}>
                            <Text style={styles.label}>Role description</Text>
                            <Text style={styles.value}>{job.roleDescription}</Text>
                        </View>
                        <View style={styles.row}>
                            <Text style={styles.label}>Date</Text>
                            <Text style={styles.value}>
                                {job.from} - {job.to}
                            </Text>
                        </View>
                    </View>
                ))}

                {/* Education */}
                {data.educationInfo?.map((edu: any, idx: number) => (
                    <View key={idx} style={styles.section}>
                        <Text style={styles.title}>Degree {idx + 1}</Text>
                        <View style={styles.row}>
                            <Text style={styles.label}>School</Text>
                            <Text style={styles.value}>{edu.universityName}</Text>
                        </View>
                        <View style={styles.row}>
                            <Text style={styles.label}>Degree</Text>
                            <Text style={styles.value}>{edu.degree}</Text>
                        </View>
                        <View style={styles.row}>
                            <Text style={styles.label}>Field of study</Text>
                            <Text style={styles.value}>{edu.field}</Text>
                        </View>
                        <View style={styles.row}>
                            <Text style={styles.label}>GPA</Text>
                            <Text style={styles.value}>{edu.GPA}</Text>
                        </View>
                        <View style={styles.row}>
                            <Text style={styles.label}>Date</Text>
                            <Text style={styles.value}>
                                {edu.from} - {edu.to}
                            </Text>
                        </View>
                    </View>
                ))}

                {/* Expertise */}
                {data.experties?.map((exp: any, idx: number) => (
                    <View key={idx} style={styles.section}>
                        <Text style={styles.title}>{exp.expert.title}</Text>
                        {exp.skills.length > 0 ? (
                            exp.skills.map((skill: any) => (
                                <Text key={skill.id}>- {skill.title}</Text>
                            ))
                        ) : (
                            <Text>-</Text>
                        )}
                    </View>
                ))}
            </Page>
        </Document>
    );
};

export default ResumePDF;
